import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CircularProgress from "@mui/material/CircularProgress";
import { requestMagicLink } from "../../api/apiClient";
import AuthLayout, { AuthAlert } from "./AuthLayout";
import AuthField from "./AuthField";

// "Email link" sign-in tab. On by default; set REACT_APP_MAGIC_LINK=false to hide it.
export const MAGIC_LINK_ENABLED = process.env.REACT_APP_MAGIC_LINK !== "false";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function MagicLinkSent({ email, onBack, onUsePassword }) {
  return (
    <div className="auth-card">
      <div className="auth-ico">
        <MailOutlineIcon />
      </div>
      <h1>Check your email</h1>
      <p className="auth-sub">
        We sent a sign-in link to <span className="auth-mail">{email}</span>. Open it on this device
        to sign in. The link expires in 15 minutes.
      </p>
      <div className="auth-acts">
        <button type="button" className="auth-btn auth-btn--p" onClick={onBack}>
          Back to sign in
        </button>
      </div>
      <div className="auth-alt">
        Rather use a password?{" "}
        <button type="button" className="auth-link" onClick={onUsePassword}>
          Sign in with password
        </button>
      </div>
    </div>
  );
}

/**
 * Password sign-in (always) plus optional email-link sign-in (flagged).
 * `doLogin({ username, password, keepSignedIn })` and `error`/`loading`
 * come from LoginPage, which owns auth state and the MFA dialog.
 */
export default function SignInForm({ doLogin, loading, error, domainTenant }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("pw");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [tried, setTried] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const switchMode = (next) => {
    setMode(next);
    setTried(false);
    setLinkError(null);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setTried(true);
    if (!username.trim() || !password) return;
    doLogin({ username, password, keepSignedIn });
  };

  const handleLinkSubmit = async (e) => {
    e.preventDefault();
    setTried(true);
    if (!EMAIL_RE.test(username.trim())) return;
    setLinkLoading(true);
    setLinkError(null);
    try {
      await requestMagicLink(username.trim());
      setLinkSent(true);
    } catch {
      setLinkError("Couldn't send the link. Please try again.");
    } finally {
      setLinkLoading(false);
    }
  };

  if (linkSent) {
    return (
      <AuthLayout domainTenant={domainTenant}>
        <MagicLinkSent
          email={username.trim()}
          onBack={() => {
            setLinkSent(false);
            switchMode("link");
          }}
          onUsePassword={() => {
            setLinkSent(false);
            switchMode("pw");
          }}
        />
      </AuthLayout>
    );
  }

  const isPw = mode === "pw";

  return (
    <AuthLayout domainTenant={domainTenant}>
      <h1>Sign in</h1>
      <p className="auth-sub">
        {isPw
          ? "Enter your credentials to continue."
          : "We'll email you a one-time link. No password needed."}
      </p>

      {MAGIC_LINK_ENABLED && (
        <div className="auth-mode" role="tablist" aria-label="Sign-in method">
          <button type="button" role="tab" aria-selected={isPw} onClick={() => switchMode("pw")}>
            <LockOutlinedIcon sx={{ fontSize: 15 }} />
            Password
          </button>
          <button type="button" role="tab" aria-selected={!isPw} onClick={() => switchMode("link")}>
            <MailOutlineIcon sx={{ fontSize: 15 }} />
            Email link
          </button>
        </div>
      )}

      {(isPw ? error : linkError) && <AuthAlert>{isPw ? error : linkError}</AuthAlert>}

      {isPw ? (
        <form className="auth-form" onSubmit={handlePasswordSubmit} noValidate>
          <AuthField
            id="username"
            label="Username or email"
            value={username}
            onChange={setUsername}
            placeholder="alex.morgan or alex@example.com"
            autoComplete="username"
            error={tried && !username.trim() ? "Enter your username or email." : ""}
          />
          <AuthField
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder="Enter your password"
            autoComplete="current-password"
            error={tried && !password ? "Enter your password." : ""}
            right={
              <button type="button" className="auth-link" onClick={() => navigate("/forgot-password")}>
                Forgot password?
              </button>
            }
          />
          <label className="auth-ck">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedIn(e.target.checked)}
            />
            Keep me signed in on this device
          </label>
          <button className="auth-btn auth-btn--p" type="submit" disabled={loading}>
            {loading ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} aria-label="Signing in" />
            ) : (
              <>
                Sign in
                <ArrowForwardIcon sx={{ fontSize: 15 }} />
              </>
            )}
          </button>
        </form>
      ) : (
        <form className="auth-form" onSubmit={handleLinkSubmit} noValidate>
          <AuthField
            id="magic-email"
            label="Email"
            type="email"
            value={username}
            onChange={setUsername}
            autoComplete="email"
            error={tried && !EMAIL_RE.test(username.trim()) ? "Enter a valid email address." : ""}
          />
          <p className="auth-note">The link works once and expires in 15 minutes.</p>
          <button className="auth-btn auth-btn--p" type="submit" disabled={linkLoading}>
            Send sign-in link
            <ArrowForwardIcon sx={{ fontSize: 15 }} />
          </button>
        </form>
      )}

      <div className="auth-alt">
        New to the Training Center?{" "}
        <button type="button" className="auth-link" onClick={() => navigate("/users/new")}>
          Create an account
        </button>
      </div>
    </AuthLayout>
  );
}
